import { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ENV } from "@/config/env";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";

const BASE_URL = ENV.API_URL;

// ─── Multilingual warning messages ───────────────────────────────────────────
const WARNINGS = [
  {
    lang: "English",
    icon: "🇺🇸",
    text: "Your account will be deactivated and your personal information will be anonymized. Your activity history (inquiries, forms, comments) will be preserved.",
  },
  {
    lang: "日本語",
    icon: "🇯🇵",
    text: "アカウントは無効化され、個人情報は匿名化されます。お問い合わせ、フォーム、コメントなどの活動履歴は保持されます。",
  },
  {
    lang: "Bahasa Indonesia",
    icon: "🇮🇩",
    text: "Akun Anda akan dinonaktifkan dan informasi pribadi Anda akan dianonimkan. Riwayat aktivitas Anda (pertanyaan, formulir, komentar) akan tetap tersimpan.",
  },
  {
    lang: "Tiếng Việt",
    icon: "🇻🇳",
    text: "Tài khoản của bạn sẽ bị vô hiệu hóa và thông tin cá nhân của bạn sẽ được ẩn danh. Lịch sử hoạt động (yêu cầu, biểu mẫu, bình luận) sẽ được lưu giữ.",
  },
];

// ─── OTP Input Component ─────────────────────────────────────────────────────
const OTP_LENGTH = 6;

interface OtpInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({ value, onChange, disabled }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusInput = (index: number) => {
    if (index >= 0 && index < OTP_LENGTH) {
      inputRefs.current[index]?.focus();
    }
  };

  const handleChange = (index: number, digit: string) => {
    if (!/^\d?$/.test(digit)) return;
    const next = [...value];
    next[index] = digit;
    onChange(next);
    if (digit && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      focusInput(index - 1);
    }
    if (e.key === "ArrowLeft") focusInput(index - 1);
    if (e.key === "ArrowRight") focusInput(index + 1);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = [...value];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    onChange(next);
    focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3">
      {Array.from({ length: OTP_LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={value[i] || ""}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          className="w-11 h-13 sm:w-13 sm:h-14 text-center text-xl font-semibold border-2 border-gray-300 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500
            disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
        />
      ))}
    </div>
  );
};

// ─── Main Page Component ─────────────────────────────────────────────────────
type Step = "request" | "verify" | "success";

export const AccountDeletionPage = () => {
  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 1 – Request verification code
  const handleRequestCode = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/login/requestWebDeletion`, { email: email.trim().toLowerCase() });
      setStep("verify");
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [email]);

  // Step 2 – Verify OTP & delete
  const handleVerifyAndDelete = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== OTP_LENGTH) {
      setError("Please enter the full 6-digit code.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/login/verifyAndExcludeAccount`, {
        email: email.trim().toLowerCase(),
        otpCode,
      });
      setStep("success");
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || "Verification failed. Please check your code and try again.");
    } finally {
      setLoading(false);
    }
  }, [email, otp]);

  // ── Success state ────────────────────────────────────────────────────────
  if (step === "success") {
    return (
      <PageShell>
        <div className="text-center py-8">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">Account Deletion Requested</h2>
          <p className="text-sm text-gray-600 max-w-sm mx-auto mb-6">
            Your account has been deactivated. Your personal information has been anonymized and you will no longer be able to log in.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700 py-2"
          >
            Return to HoRenSo+
          </Link>
        </div>
      </PageShell>
    );
  }

  // ── Request / Verify steps ───────────────────────────────────────────────
  return (
    <PageShell>
      {/* Multilingual warnings */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <svg className="h-5 w-5 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <span className="font-semibold text-red-800 text-sm">Warning / 警告 / Peringatan / Cảnh báo</span>
        </div>
        <ul className="space-y-2">
          {WARNINGS.map((w) => (
            <li key={w.lang} className="text-xs text-red-700 leading-relaxed">
              <span className="mr-1.5">{w.icon}</span>
              <span className="font-medium">{w.lang}:</span> {w.text}
            </li>
          ))}
        </ul>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 mb-4 bg-red-50 text-red-800 border border-red-200 rounded-lg text-sm">
          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
          {error}
        </div>
      )}

      {step === "request" && (
        <form onSubmit={handleRequestCode} className="space-y-4">
          <p className="text-sm text-gray-600">
            Enter the email address associated with your HoRenSo+ account. We will send a one-time verification code to confirm your identity.
          </p>
          <Input
            label="Account Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <Button
            type="submit"
            loading={loading}
            className="w-full !bg-red-600 hover:!bg-red-700 focus:!ring-red-500"
          >
            Send Verification Code
          </Button>
        </form>
      )}

      {step === "verify" && (
        <form onSubmit={handleVerifyAndDelete} className="space-y-5">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              A verification code was sent to <span className="font-medium text-gray-900">{email}</span>.
              Enter the 6-digit code below.
            </p>
          </div>

          <OtpInput value={otp} onChange={setOtp} disabled={loading} />

          <Button
            type="submit"
            loading={loading}
            disabled={otp.join("").length !== OTP_LENGTH}
            className="w-full !bg-red-600 hover:!bg-red-700 focus:!ring-red-500"
          >
            Permanently Delete My Account
          </Button>

          <button
            type="button"
            onClick={() => { setStep("request"); setOtp(Array(OTP_LENGTH).fill("")); setError(""); }}
            className="w-full text-sm text-gray-500 hover:text-gray-700 active:text-gray-800 transition-colors py-2"
          >
            ← Use a different email
          </button>
        </form>
      )}

      {/* Cancel link */}
      <div className="mt-6 text-center">
        <Link to="/" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium inline-block py-2">
          Cancel — return to HoRenSo+
        </Link>
      </div>
    </PageShell>
  );
};

// ─── Page Shell (layout wrapper) ─────────────────────────────────────────────
const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-[100dvh] flex items-start pt-8 md:items-center md:pt-0 justify-center bg-gray-50 px-4 py-8">
    <div className="w-full max-w-md">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-8">
        {/* Brand header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">HoRenSo+</h1>
          <p className="text-xs text-gray-400 mt-0.5">Consult · Connect · Communicate</p>
          <div className="mt-4 border-t border-gray-100 pt-4">
            <h2 className="text-lg font-semibold text-red-700">Account Deletion</h2>
          </div>
        </div>
        {children}
      </div>

      {/* Footer */}
      <p className="text-center text-xs text-gray-400 mt-4">
        © {new Date().getFullYear()} HoRenSo+. All rights reserved.
      </p>
    </div>
  </div>
);
